// Registro central de catalogos "planos" (listas maestras que useCatalogoPlano
// cachea en localStorage). Es la fuente de verdad para:
//   - resolver endpoint + cacheKey a partir de un slug logico
//   - invalidar la cache cuando se crea / edita / borra / importa un registro
//
// El slug coincide con el `type` de <DialogForm> y con el `entityKey` de
// useCrudDelete, de modo que la invalidacion se engancha en un solo punto por
// operacion (crear/editar -> DialogForm, borrar -> useCrudDelete).

export interface CatalogRegistryEntry {
  /** Endpoint por defecto para cargar la lista completa del catalogo. */
  endpoint: string
  /**
   * Todas las claves de localStorage bajo las que este catalogo puede quedar
   * cacheado. La primera es la CANONICA (la que usa useCatalogoPlano cuando se
   * lo invoca por slug); el resto son alias historicos que igual se limpian al
   * invalidar para no dejar copias viejas dando vueltas.
   */
  cacheKeys: string[]
}

export const CATALOG_REGISTRY = {
  measurements:             { endpoint: '/meassurement',                                          cacheKeys: ['catalogo-measurement'] },
  machines:                 { endpoint: '/machine',                                               cacheKeys: ['catalogo-machines'] },
  'machines-types':         { endpoint: '/machineType',                                           cacheKeys: ['catalogo-machine-types'] },
  materials:                { endpoint: '/material',                                              cacheKeys: ['catalogo-materials'] },
  densities:                { endpoint: '/density',                                               cacheKeys: ['catalogo-densities'] },
  thicknesses:              { endpoint: '/thickness',                                             cacheKeys: ['catalogo-thicknesses'] },
  processes:                { endpoint: '/process',                                               cacheKeys: ['catalogo-processes'] },
  workstations:             { endpoint: '/workstation',                                           cacheKeys: ['catalogo-workstations', 'catalogo-workstations-checkr'] },
  suppliers:                { endpoint: '/supplier',                                              cacheKeys: ['catalogo-suppliers'] },
  products:                 { endpoint: '/product',                                               cacheKeys: ['catalogo-products', 'catalogo-products-children'] },
  'units-of-measurement':   { endpoint: '/unitOfMeasurement',                                     cacheKeys: ['catalogo-units-of-measurement'] },
  'open-cell-specs':        { endpoint: '/OpenCellSpec/get-all',                                  cacheKeys: ['catalogo-open-cell-specs'] },
  'rex3-specs':             { endpoint: '/Rex3Specs/get-all',                                     cacheKeys: ['catalogo-rex3-specs'] },
  'certificates-templates': { endpoint: '/certificates/templates',                               cacheKeys: ['catalogo-certificates-templates'] },
  customers:                { endpoint: '/customers/get-all?isActive=true&pageNumber=1&pageSize=200', cacheKeys: ['catalogo-customers'] },
  roles:                    { endpoint: '/role',                                                  cacheKeys: ['catalogo-roles'] },
  dispositions:             { endpoint: '/dispositions/get-active',                               cacheKeys: ['catalogo-dispositions'] },
  'non-conformities':       { endpoint: '/nonConformities/get-active',                            cacheKeys: ['catalogo-non-conformities'] },
} satisfies Record<string, CatalogRegistryEntry>

export type CatalogSlug = keyof typeof CATALOG_REGISTRY

/**
 * cacheKey (canonica o alias) -> slug. Permite que useCatalogoPlano sepa a que
 * catalogo pertenece aunque se lo siga llamando con la clave string "legacy".
 */
export const CACHEKEY_TO_SLUG: Record<string, CatalogSlug> = Object.entries(CATALOG_REGISTRY)
  .reduce((acc, [slug, entry]) => {
    for (const key of entry.cacheKeys) acc[key] = slug as CatalogSlug
    return acc
  }, {} as Record<string, CatalogSlug>)

/** Devuelve la entrada del registro para un slug, o undefined si no existe. */
export function getCatalogEntry(slug: string): CatalogRegistryEntry | undefined {
  return (CATALOG_REGISTRY as Record<string, CatalogRegistryEntry>)[slug]
}
